import React, { useEffect, useState } from 'react'
import { Button, Modal } from '@intelie/ui'

import i18n from 'live/services/i18n'
import LiveSelect from 'live/components/forms/ui/select'
import FormGroup from 'live/components/forms/ui/form-group'
import globalNotification from 'live/services/global-notification'

import { Perspective, Template } from '../../../types'
import { usePerspectives } from '../../../hooks/usePerspectives'
import { ModalActions, TemplateInfoModalContent } from '../../styles'

interface TemplateInfoModalProps {
    show: boolean
    template: Template
    setShow: React.Dispatch<React.SetStateAction<boolean>>
    setTemplate: React.Dispatch<React.SetStateAction<Template>>
}

const TemplateInfoModal: React.FC<TemplateInfoModalProps> = ({
    show,
    template,
    setShow,
    setTemplate
}: TemplateInfoModalProps) => {
    const perspectives = usePerspectives()

    const [{ alias, color, perspective, description }, setTemplateInfo] = useState({
        alias: template.alias,
        color: template.color,
        perspective: template.perspective,
        description: template.description
    })

    const handleSaveChanges = async (): Promise<void> => {
        if (validateFields()) {
            const templateCopy = { ...template }

            templateCopy.alias = alias
            templateCopy.color = color
            templateCopy.perspective = perspective
            templateCopy.description = description

            setTemplate(templateCopy)
            setShow(false)

            return
        }

        globalNotification.error(i18n('You must fill all required fields'), 5)
    }

    const handleCancel = (): void => {
        setTemplateInfo({
            alias: template.alias,
            color: template.color,
            perspective: template.perspective,
            description: template.description
        })
        setShow(false)
    }

    const validateFields = (): boolean => {
        if (alias.length <= 0) {
            return false
        }

        if (!perspective) {
            return false
        }

        return true
    }

    useEffect(() => {
        setTemplateInfo({
            alias: template.alias,
            color: template.color,
            perspective: template.perspective,
            description: template.description
        })
    }, [template])

    return (
        <Modal closable size="sm" show={show} onSave={handleSaveChanges} onHide={() => handleCancel()}>
            <Modal.Header closeButton title={`${i18n('Template information')}`} />
            <TemplateInfoModalContent>
                <div className="name-color-section">
                    <div className="name-section">
                        <label htmlFor="alias" className="title required">
                            {i18n('Alias')}
                        </label>
                        <FormGroup
                            validationState={alias && alias.length > 0 ? 'success' : 'error'}
                            type="text"
                            id="alias"
                            name="alias"
                            value={alias}
                            placeholder={i18n('Type...')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setTemplateInfo({ alias: e.target.value, color, perspective, description })
                            }
                        />
                    </div>
                    <div className="color-section">
                        <label htmlFor="color" className="title required">
                            {i18n('Color')}
                        </label>
                        <FormGroup
                            type="color"
                            name="color"
                            id="color"
                            value={color}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setTemplateInfo({ alias, color: e.target.value, perspective, description })
                            }
                        />
                    </div>
                </div>
                <label htmlFor="perspective" className="title required">
                    {i18n('Perspective')}
                </label>
                <LiveSelect
                    id="perspective"
                    clearable={true}
                    value={perspective}
                    options={perspectives}
                    help={`${i18n("Templates must match the dashboard's perspective")}.`}
                    placeholder={i18n('Select...')}
                    onChange={(_: string, option: Perspective) =>
                        setTemplateInfo({ alias, color, perspective: option, description })
                    }
                />
                <FormGroup
                    componentClass="textarea"
                    value={description}
                    label={i18n('Description')}
                    name="description"
                    className="textarea textarea-md"
                    placeholder={i18n('Type...')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTemplateInfo({ alias, color, perspective, description: e.target.value })
                    }
                />
                <ModalActions>
                    <Button onClick={handleCancel}>{i18n('Cancel')}</Button>
                    <Button variant="primary" disabled={!alias || !color || !perspective} onClick={handleSaveChanges}>
                        {i18n('Confirm')}
                    </Button>
                </ModalActions>
            </TemplateInfoModalContent>
        </Modal>
    )
}

export default TemplateInfoModal
